import { useEffect } from "react";
import { Quote } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import FinalCTA from "@/components/landing/FinalCTA";
import TestimonialVideoPlayer from "@/components/landing/TestimonialVideoPlayer";
import { TESTIMONIALS } from "@/data/testimonials";

/**
 * Página /testimonios-eos — reutiliza el formato de "detalle de artículo" del blog
 * (hero navy con título/bajada + sección de contenido blanca + horizonte de contacto).
 * Cada testimonio: video vertical a la izquierda (mismo reproductor del Hero de la Home),
 * nombre/cargo/empresa y 2 quotes a la derecha.
 */
export default function Testimonios() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "Testimonios | Carlos Garcín — Professional EOS Implementer",
      description:
        "Empresarios que implementaron EOS con Carlos Garcín y transformaron la forma en que dirigen sus negocios. Resultados reales, en sus propias palabras.",
      path: "/testimonios-eos",
      keywords: "testimonios EOS, casos de éxito EOS Chile, clientes Carlos Garcín, Entrepreneurial Operating System resultados",
    });
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: "https://garcin.cl/" },
      { "@type": "ListItem", position: 2, name: "Testimonios", item: "https://garcin.cl/testimonios-eos" },
    ],
  };

  return (
    <>
      <Header />
      <main>
        <section className="bg-[#0A1128] pt-28 pb-16" data-testid="testimonials-hero">
          <div className="container-eos">
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase text-[#F26522]">
              Testimonios
            </span>
            <h1 className="mt-4 font-display font-extrabold text-white leading-[1.1] tracking-[-0.02em] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
              Resultados reales, en palabras de <span className="text-[#F26522]">quienes los vivieron</span>
            </h1>
            <p className="mt-5 max-w-2xl text-slate-300 text-base sm:text-lg leading-relaxed">
              Empresarios y equipos de liderazgo que implementaron EOS y transformaron la forma en que dirigen sus negocios.
            </p>
          </div>
        </section>

        <section className="pt-14 sm:pt-16 lg:pt-20 pb-20 sm:pb-24 lg:pb-28 bg-white" data-testid="testimonials-content">
          <div className="container-eos">
            <div className="space-y-16 lg:space-y-20 divide-y divide-slate-200">
              {TESTIMONIALS.map((t, idx) => (
                <div
                  key={t.id}
                  data-testid={`testimonial-card-${t.id}`}
                  className={`reveal grid lg:grid-cols-12 gap-8 lg:gap-10 items-center ${idx > 0 ? "pt-16 lg:pt-20" : ""}`}
                >
                  <div className="lg:col-span-4">
                    <TestimonialVideoPlayer videoId={t.videoId} posterImage={t.poster} name={t.name} />
                  </div>

                  <div className="lg:col-span-8">
                    <p className="font-display font-bold text-xl sm:text-2xl text-navy-800" data-testid={`testimonial-name-${t.id}`}>
                      {t.name}
                    </p>
                    <p className="mt-1 text-sm sm:text-base text-slate-500">
                      {t.role}
                      {t.company && (
                        <>
                          {" "}·{" "}
                          <span className="text-[#F26522] font-semibold" data-testid={`testimonial-company-${t.id}`}>
                            {t.company}
                          </span>
                        </>
                      )}
                    </p>

                    <div className="mt-6 space-y-6">
                      {t.quotes.map((q, i) => (
                        <blockquote key={i} data-testid={`testimonial-quote-${t.id}-${i + 1}`} className="flex gap-3">
                          <Quote className="w-5 h-5 text-[#F26522] shrink-0 mt-0.5" fill="currentColor" />
                          <p className="text-slate-700 leading-relaxed text-lg sm:text-xl italic">
                            {q}
                            <Quote
                              className="inline-block w-3.5 h-3.5 text-[#F26522] ml-1.5 rotate-180 relative -top-0.5"
                              fill="currentColor"
                            />
                          </p>
                        </blockquote>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </>
  );
}
